// src/pages/EarlyAccessAdminPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db as defaultDb, auth as defaultAuth } from "../firebase";

const OWNER_EMAIL = "juanquiros151@gmail.com";
const BRAND = "#00675f";
const BRAND_2 = "#00897f";

function formatDate(value) {
  try {
    if (!value) return "—";
    const date =
      typeof value.toDate === "function"
        ? value.toDate()
        : value.seconds
          ? new Date(value.seconds * 1000)
          : new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("es-PA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return "—";
  }
}

function dateMs(value) {
  try {
    if (!value) return 0;
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (value.seconds) return value.seconds * 1000;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch (_) {
    return 0;
  }
}

function statusLabel(status) {
  const s = String(status || "new").toLowerCase();
  if (s === "contacted") return "Contactado";
  if (s === "converted") return "Convertido";
  if (s === "archived") return "Archivado";
  return "Nuevo";
}

function statusClass(status) {
  const s = String(status || "new").toLowerCase();
  if (s === "contacted") return "is-contacted";
  if (s === "converted") return "is-converted";
  if (s === "archived") return "is-archived";
  return "is-new";
}

function normalizePhoneForWhatsApp(phone) {
  return String(phone || "").replace(/[^\d]/g, "");
}

function getReadableError(err) {
  const code = err?.code ? String(err.code) : "";
  const message = err?.message ? String(err.message) : String(err || "");
  if (code.includes("permission-denied") || message.toLowerCase().includes("permission")) {
    return "Permisos denegados. Confirma que publicaste las reglas nuevas y que estás conectado con juanquiros151@gmail.com.";
  }
  if (message.toLowerCase().includes("index")) {
    return "Firestore pidió un índice. Esta versión evita orderBy para reducir ese riesgo. Intenta actualizar.";
  }
  return message || "Error desconocido.";
}

export default function EarlyAccessAdminPage({ db = defaultDb, auth = defaultAuth }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [rawError, setRawError] = useState("");
  const [filter, setFilter] = useState("all");

  const currentEmail = String(auth?.currentUser?.email || "").trim().toLowerCase();
  const canView = currentEmail === OWNER_EMAIL;

  const filteredLeads = useMemo(() => {
    if (filter === "all") return leads;
    return leads.filter((lead) => String(lead.status || "new").toLowerCase() === filter);
  }, [leads, filter]);

  const counts = useMemo(() => {
    const base = { all: leads.length, new: 0, contacted: 0, converted: 0, archived: 0 };
    leads.forEach((lead) => {
      const s = String(lead.status || "new").toLowerCase();
      if (base[s] != null) base[s] += 1;
    });
    return base;
  }, [leads]);

  const loadLeads = async () => {
    setError("");
    setRawError("");
    setLoading(true);

    try {
      // No orderBy here: avoids index issues and is more robust while validating rules.
      const snap = await getDocs(collection(db, "earlyAccessLeads"));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .sort((a, b) => dateMs(b.createdAt) - dateMs(a.createdAt));

      setLeads(list);
    } catch (err) {
      console.error("[EarlyAccessAdmin] load failed", err);
      setError(getReadableError(err));
      setRawError(String(err?.code || err?.message || err || ""));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const updateStatus = async (leadId, status) => {
    setBusyId(leadId);
    try {
      await updateDoc(doc(db, "earlyAccessLeads", leadId), {
        status,
        updatedAt: serverTimestamp(),
      });
      setLeads((prev) => prev.map((lead) => (lead.id === leadId ? { ...lead, status } : lead)));
    } catch (err) {
      console.error("[EarlyAccessAdmin] update failed", err);
      setError(getReadableError(err));
      setRawError(String(err?.code || err?.message || err || ""));
    } finally {
      setBusyId("");
    }
  };

  const removeLead = async (leadId) => {
    const ok = window.confirm("¿Seguro que quieres eliminar este lead?");
    if (!ok) return;

    setBusyId(leadId);
    try {
      await deleteDoc(doc(db, "earlyAccessLeads", leadId));
      setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
    } catch (err) {
      console.error("[EarlyAccessAdmin] delete failed", err);
      setError(getReadableError(err));
      setRawError(String(err?.code || err?.message || err || ""));
    } finally {
      setBusyId("");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Fecha", "Nombre", "Empresa", "Teléfono", "Correo", "Tipo", "Estado", "Mensaje"],
      ...filteredLeads.map((lead) => [
        formatDate(lead.createdAt),
        lead.name || "",
        lead.businessName || "",
        lead.phone || "",
        lead.email || "",
        lead.businessType || "",
        statusLabel(lead.status),
        String(lead.message || "").replace(/\s+/g, " ").trim(),
      ]),
    ];

    const csv = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "loop-early-access-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canView) {
    return (
      <main className="early-admin-page">
        <style>{styles}</style>
        <section className="early-admin-denied">
          <div className="early-admin-logo">∞</div>
          <h1>Acceso privado</h1>
          <p>Esta página solo está disponible para {OWNER_EMAIL}.</p>
          <p className="early-admin-current">Sesión actual: {currentEmail || "sin sesión detectada"}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="early-admin-page">
      <style>{styles}</style>

      <section className="early-admin-shell">
        <div className="early-admin-header">
          <div>
            <div className="early-admin-kicker">Loop CRM</div>
            <h1>Early access leads</h1>
            <p>Solicitudes públicas de negocios interesados en entrar al lanzamiento privado.</p>
            <div className="early-admin-session">Viendo como: {currentEmail}</div>
          </div>

          <div className="early-admin-actions">
            <button type="button" onClick={loadLeads} disabled={loading}>
              Actualizar
            </button>
            <button type="button" onClick={exportCsv} disabled={!filteredLeads.length}>
              Exportar CSV
            </button>
          </div>
        </div>

        <div className="early-admin-stats">
          {[
            ["all", "Todos", counts.all],
            ["new", "Nuevos", counts.new],
            ["contacted", "Contactados", counts.contacted],
            ["converted", "Convertidos", counts.converted],
            ["archived", "Archivados", counts.archived],
          ].map(([key, label, value]) => (
            <button key={key} type="button" className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>
              <strong>{value}</strong>
              <span>{label}</span>
            </button>
          ))}
        </div>

        {error ? (
          <div className="early-admin-error">
            <strong>{error}</strong>
            {rawError ? <small>{rawError}</small> : null}
          </div>
        ) : null}

        {loading ? (
          <div className="early-admin-loading">Cargando leads...</div>
        ) : filteredLeads.length === 0 ? (
          <div className="early-admin-empty">
            No hay leads para este filtro.
            <br />
            <span>Para probar: entra a /early-access, manda una solicitud y vuelve aquí a tocar “Actualizar”.</span>
          </div>
        ) : (
          <div className="early-admin-grid">
            {filteredLeads.map((lead) => {
              const whatsappNumber = normalizePhoneForWhatsApp(lead.phone);
              const whatsappText = encodeURIComponent(
                `Hola ${lead.name || ""}, soy Juan de Loop. Vi tu solicitud de early access para ${lead.businessName || "tu negocio"} y quería coordinar los próximos pasos.`
              );
              const whatsappHref = whatsappNumber ? `https://wa.me/${whatsappNumber}?text=${whatsappText}` : "";

              return (
                <article className="early-admin-card" key={lead.id}>
                  <div className="early-admin-card-top">
                    <div>
                      <div className="early-admin-business">{lead.businessName || "Sin empresa"}</div>
                      <div className="early-admin-name">{lead.name || "Sin nombre"}</div>
                    </div>
                    <span className={`early-admin-status ${statusClass(lead.status)}`}>
                      {statusLabel(lead.status)}
                    </span>
                  </div>

                  <div className="early-admin-meta">
                    <div><span>Teléfono</span><strong>{lead.phone || "—"}</strong></div>
                    <div><span>Correo</span><strong>{lead.email || "—"}</strong></div>
                    <div><span>Tipo</span><strong>{lead.businessType || "—"}</strong></div>
                    <div><span>Fecha</span><strong>{formatDate(lead.createdAt)}</strong></div>
                  </div>

                  {lead.message ? <p className="early-admin-message">{lead.message}</p> : null}

                  <div className="early-admin-card-actions">
                    {whatsappHref ? <a href={whatsappHref} target="_blank" rel="noreferrer">WhatsApp</a> : null}
                    {lead.email ? <a href={`mailto:${lead.email}`}>Email</a> : null}
                    <button type="button" onClick={() => updateStatus(lead.id, "contacted")} disabled={busyId === lead.id}>Contactado</button>
                    <button type="button" onClick={() => updateStatus(lead.id, "converted")} disabled={busyId === lead.id}>Convertido</button>
                    <button type="button" onClick={() => updateStatus(lead.id, "archived")} disabled={busyId === lead.id}>Archivar</button>
                    <button type="button" className="danger" onClick={() => removeLead(lead.id)} disabled={busyId === lead.id}>Eliminar</button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}

const styles = `
  .early-admin-page {
    min-height: calc(100dvh - 86px);
    padding: 32px 18px calc(42px + env(safe-area-inset-bottom, 0px));
    background:
      radial-gradient(circle at top left, rgba(0,103,95,0.12), transparent 30%),
      linear-gradient(180deg, #f8fafc 0%, #ffffff 58%, #f8fafc 100%);
    color: #0f172a;
    box-sizing: border-box;
  }

  .early-admin-shell { width: min(100%, 1180px); margin: 0 auto; }

  .early-admin-header {
    display: flex; align-items: flex-end; justify-content: space-between;
    gap: 18px; margin-bottom: 22px;
  }

  .early-admin-kicker {
    color: ${BRAND}; font-size: 12px; font-weight: 950;
    letter-spacing: 0.14em; text-transform: uppercase; margin-bottom: 9px;
  }

  .early-admin-header h1 {
    margin: 0; font-size: clamp(38px, 5vw, 64px); line-height: 0.96;
    letter-spacing: -0.07em; font-weight: 950;
  }

  .early-admin-header p {
    margin: 12px 0 0; color: #64748b; font-size: 15px;
    line-height: 1.45; font-weight: 750; max-width: 680px;
  }

  .early-admin-session, .early-admin-current {
    margin-top: 10px; color: #00675f; font-size: 12px; font-weight: 900;
  }

  .early-admin-actions { display: flex; gap: 10px; flex-wrap: wrap; }

  .early-admin-actions button,
  .early-admin-card-actions button,
  .early-admin-card-actions a {
    min-height: 40px; border-radius: 999px; border: 1px solid #e2e8f0;
    background: #ffffff; color: #0f172a; padding: 0 14px; font-size: 13px;
    font-weight: 900; display: inline-flex; align-items: center; justify-content: center;
    text-decoration: none; cursor: pointer;
  }

  .early-admin-actions button:first-child {
    background: linear-gradient(135deg, ${BRAND}, ${BRAND_2}); color: #ffffff;
    border-color: transparent; box-shadow: 0 14px 30px rgba(0,103,95,0.20);
  }

  .early-admin-stats {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 18px;
  }

  .early-admin-stats button {
    border: 1px solid rgba(226,232,240,0.95); background: rgba(255,255,255,0.88);
    border-radius: 22px; padding: 14px; text-align: left; cursor: pointer;
    box-shadow: 0 10px 26px rgba(15,23,42,0.055);
  }

  .early-admin-stats button.active { border-color: rgba(0,103,95,0.32); background: #f0fdfa; }
  .early-admin-stats strong { display: block; font-size: 28px; line-height: 1; font-weight: 950; color: #0f172a; }
  .early-admin-stats span { display: block; margin-top: 6px; color: #64748b; font-size: 12px; font-weight: 850; }

  .early-admin-grid { display: grid; gap: 14px; }

  .early-admin-card {
    border: 1px solid rgba(226,232,240,0.95); background: rgba(255,255,255,0.95);
    border-radius: 28px; box-shadow: 0 16px 44px rgba(15,23,42,0.07); padding: 18px;
  }

  .early-admin-card-top {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 14px; margin-bottom: 14px;
  }

  .early-admin-business { color: ${BRAND}; font-size: 20px; font-weight: 950; letter-spacing: -0.04em; }
  .early-admin-name { margin-top: 4px; color: #64748b; font-size: 13px; font-weight: 800; }

  .early-admin-status {
    border-radius: 999px; padding: 7px 10px; font-size: 11px; font-weight: 950;
    text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap;
  }

  .early-admin-status.is-new { background: #eff6ff; color: #1d4ed8; }
  .early-admin-status.is-contacted { background: #fef3c7; color: #92400e; }
  .early-admin-status.is-converted { background: #dcfce7; color: #047857; }
  .early-admin-status.is-archived { background: #f1f5f9; color: #64748b; }

  .early-admin-meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }

  .early-admin-meta div {
    background: #f8fafc; border: 1px solid #eef2f7;
    border-radius: 18px; padding: 11px; min-width: 0;
  }

  .early-admin-meta span {
    display: block; color: #94a3b8; font-size: 10px; font-weight: 950;
    letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 5px;
  }

  .early-admin-meta strong {
    display: block; color: #0f172a; font-size: 12px;
    line-height: 1.25; font-weight: 850; overflow-wrap: anywhere;
  }

  .early-admin-message {
    margin: 13px 0 0; color: #475569; background: #f8fafc;
    border-left: 3px solid ${BRAND}; border-radius: 14px; padding: 12px;
    font-size: 13px; line-height: 1.45; font-weight: 700;
  }

  .early-admin-card-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 14px; }
  .early-admin-card-actions a:first-child { background: #ecfdf5; border-color: #bbf7d0; color: #047857; }
  .early-admin-card-actions button.danger { color: #dc2626; border-color: #fecaca; background: #fff7f7; }

  .early-admin-error,
  .early-admin-loading,
  .early-admin-empty,
  .early-admin-denied {
    border: 1px solid rgba(226,232,240,0.95); background: rgba(255,255,255,0.95);
    border-radius: 28px; padding: 24px; color: #64748b; font-weight: 800;
    box-shadow: 0 16px 44px rgba(15,23,42,0.07);
  }

  .early-admin-empty span { display: inline-block; margin-top: 8px; font-size: 12px; color: #94a3b8; }

  .early-admin-error {
    border-color: #fecaca; background: #fff7f7; color: #b91c1c; margin-bottom: 14px;
  }

  .early-admin-error small {
    display: block; margin-top: 8px; color: #991b1b; font-size: 12px; font-weight: 750;
  }

  .early-admin-denied { width: min(100%, 520px); margin: 60px auto; text-align: center; }
  .early-admin-denied h1 { margin: 12px 0 0; font-size: 38px; letter-spacing: -0.06em; color: #0f172a; }
  .early-admin-denied p { margin: 10px 0 0; }

  .early-admin-logo {
    margin: 0 auto; width: 58px; height: 58px; border-radius: 22px;
    display: grid; place-items: center; background: linear-gradient(135deg, ${BRAND}, ${BRAND_2});
    color: #ffffff; font-size: 30px; font-weight: 950;
  }

  @media (max-width: 820px) {
    .early-admin-header { display: block; }
    .early-admin-actions { margin-top: 16px; }
    .early-admin-stats { grid-template-columns: repeat(2, 1fr); }
    .early-admin-meta { grid-template-columns: 1fr 1fr; }
  }

  @media (max-width: 520px) {
    .early-admin-page { padding: 22px 12px calc(32px + env(safe-area-inset-bottom, 0px)); }
    .early-admin-stats, .early-admin-meta { grid-template-columns: 1fr; }
    .early-admin-card-top { display: block; }
    .early-admin-status { display: inline-flex; margin-top: 10px; }
  }
`;
